/**
 * The wind at altitude, leg by leg — the other half of "what is the weather
 * doing", and the half the route is actually flown through.
 *
 * It is a strip and not a table: the numbers that matter are three per leg
 * (how high, from where, how strong) and a pilot checking them is scanning, not
 * reading. The NavLog is where the same forecast turns into GS and ETO.
 *
 * A leg shows its W/V only while the FORECAST still owns that cell. A W/V the
 * pilot typed is the pilot's and is not ours to caption as a forecast — it
 * simply is not in this strip, and it is in the NavLog and on the form where it
 * belongs.
 *
 * Open-Meteo's licence asks for a link beside any display of its data, so the
 * one credit line under the strip is not decoration and does not move.
 */
import { useMemo } from 'react';

import { Freshness } from '../../components/Freshness';
import { LEG_NUMBERS, legField } from '../../fields';
import { useT } from '../../i18n';
import { usePlanRev, usePlanStore, useRoute } from '../../planContext';
import { MAX_ROUTE_LEGS } from '../../route';
import { fmtTempC, legAltFt } from '../../winds';
import { useWinds } from '../../windsContext';

/** German thousands, the narrow no-break space the rest of the app prints. */
const feet = (n: number): string => Math.round(n).toLocaleString('de-DE').replace(/\./g, ' ');

interface Row {
  leg: number;
  from: string;
  to: string;
  alt: string;
  wv: string;
  temp: string;
  stale: boolean;
}

export function WindsAloft() {
  const t = useT();
  const store = usePlanStore();
  const rev = usePlanRev();
  const route = useRoute();
  const winds = useWinds();

  const waypoints = route?.waypoints ?? [];
  const legs = Math.min(Math.max(0, waypoints.length - 1), MAX_ROUTE_LEGS);

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (const j of LEG_NUMBERS) {
      if (j > legs) break;
      const a = waypoints[j - 1];
      const b = waypoints[j];
      if (!a || !b) break;
      const id = legField(j, 'wind');
      const own = store.derivedSource(id) === 'winds' ? store.val(id) : '';
      const leg = winds?.legs.get(j);
      out.push({
        leg: j,
        from: a.name,
        to: b.name,
        alt: feet(legAltFt(store.val(legField(j, 'alt')), a.alt_ft, b.alt_ft)),
        wv: leg?.wv ?? own,
        temp: leg?.temp_c === undefined ? '' : fmtTempC(leg.temp_c),
        stale: winds?.stale.has(j) === true && own !== '',
      });
    }
    return out;
    // `rev` is the store's change signal: the W/V and Höhe cells are half of this
  }, [legs, waypoints, store, winds, rev]);

  if (rows.length === 0) return null;

  return (
    <section className="wx-winds">
      <div className="wx-block-head">
        <h3 className="ae-eyebrow">{t('winds.title')}</h3>
        {/* status only — the forecast has exactly one ⟳, and it is not here */}
        <Freshness family="winds" action={false} label={false} />
      </div>
      <ul className="wx-legs">
        {rows.map(r => (
          <li key={r.leg}>
            <span className="wx-leg-no ae-num">{r.leg}</span>
            <span className="wx-leg-names">
              {r.from} <span aria-hidden="true">→</span> {r.to}
            </span>
            <span className="wx-leg-vals ae-num">
              {[r.alt + ' ft', r.wv, r.temp].filter(Boolean).join(' · ') || t('winds.none')}
            </span>
            {r.stale ? <span className="wx-stale">{t('winds.stale')}</span> : null}
          </li>
        ))}
      </ul>
      <p className="wx-credit">
        <a href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer">
          {t('winds.credit')}
        </a>
      </p>
    </section>
  );
}
